import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { Shell } from '../../../componentes/Shell.jsx';
import { consultarCategorias } from '../../../nucleo/api/nucleoApi.js';
import { CampoDeFecha } from '../../../nucleo/fecha/CampoDeFecha.jsx';
import { formatearFecha, obtenerFechaDeNegocioHoy } from '../../../nucleo/fecha/fechaLocal.js';
import {
  escribirContextoDelHistorial, leerContextoDelHistorial,
} from '../../../nucleo/navegacion/contextoDeNavegacion.js';
import { rutaDeAltaDesde, useContextoDeListado } from '../../../nucleo/navegacion/useContextoDeRegreso.js';
import {
  ListadoActualizando, ListadoCargando, ListadoConError,
} from '../../../nucleo/listados/EstadosDeListado.jsx';
import { ESTADO_DE_LISTADO, useConsultaDeListado } from '../../../nucleo/listados/useConsultaDeListado.js';
import { consultarCuentas } from '../../cuentas/api/cuentasApi.js';
import { consultarMovimientos, RENGLONES_REPARTIDOS, TIPOS } from '../api/movimientosApi.js';
import { ComboboxDeCuenta } from '../componentes/ComboboxDeCuenta.jsx';
import { SelectorConBuscador } from '../componentes/SelectorConBuscador.jsx';
import { PERFIL_DE_CATEGORIA } from '../logica/perfilesDeSelector.js';
import {
  agruparPorDia, claseDeColorDelImporte, importe as formatearImporte, NOMBRE_DE_MONEDA,
  presentacionDelImporte, primerDiaDelMes, tituloDelMovimiento,
} from '../formato.js';
// RF-215, RN-614 · color del importe por tipo. La regla del traspaso (azul) no
// existía en la hoja del núcleo y se escribe una sola vez, compartida con el
// Tablero. `base.css` no se toca: está fuera de la lista cerrada de D-309.
import '../../../estilos/importes-por-tipo.css';

const TAMANO_DE_PAGINA = 50;

/**
 * LEYENDA DE LA BARRA DE TOTALES (D-111, RN-189a).
 *
 * TEXTO LITERAL Y ÚNICO: no se reescribe, no se abrevia y no se pluraliza según el
 * caso. Es UNA SOLA CADENA, con la misma disciplina que el formato único de importe
 * (RN-146, D-88) y, por lo mismo, sin `US$` (RN-147).
 *
 * Vive en una constante y no incrustada en el JSX para que la prueba afirme EL
 * MISMO texto que se pinta, y no una copia que pueda separarse de él.
 */
const LEYENDA_DE_CANCELADOS = 'Los movimientos cancelados no suman en los totales.';

/**
 * ¿Se muestra la leyenda? (RN-189b, condición EXACTA).
 *
 * SI Y SOLO SI hay renglones en el conjunto (`totalRegistros > 0`) Y al menos una
 * entrada de `totalesPorMoneda` trae `cantidadMovimientos === 0`. Esa combinación
 * significa —y solo puede significar— que esa moneda está presente en el conjunto
 * pero NINGUNO de sus renglones suma, es decir, que todos son cancelados (RN-188a,
 * RN-188b, RN-188c).
 *
 * TRES COSAS QUE ESTA FUNCIÓN NO HACE, y cada una sería un defecto:
 *   1. NO mira los renglones de la página ni sus importes. `cantidadMovimientos` es
 *      EL ÚNICO DISCRIMINADOR: la pantalla no deduce ni calcula (RN-165b, RN-189i).
 *      Contar cancelados de la página está prohibido: la página trae 50 renglones y
 *      el conjunto puede tener muchos más.
 *   2. NO aparece con el CONJUNTO VACÍO (RN-189c). Ahí las entradas en `0.00` vienen
 *      del catálogo (RN-187c) y no hay ningún cancelado que explicar; la leyenda
 *      junto al estado vacío sería una mentira.
 *   3. NO aparece con un `0.00` LEGÍTIMO (RN-189d): una moneda cuyos importes se
 *      cancelan entre sí tiene `cantidadMovimientos > 0` y queda fuera de la
 *      condición. Ese cero es un resultado real, no una ausencia.
 *
 * Con `renglonesRepartidos = SOLO` no puede cumplirse nunca, y NO se programa como
 * excepción: ningún cancelado es un renglón repartido (RN-116, RN-174b, RN-186c), de
 * modo que toda entrada trae `cantidadMovimientos > 0`. Añadir aquí un `if` por SOLO
 * contradiría «`cantidadMovimientos` es el único discriminador» y, peor, TAPARÍA el
 * defecto que RN-189f manda dejar a la vista si alguna vez apareciera.
 *
 * NADA NUEVO DEL SERVIDOR: los dos campos ya viajan en la respuesta (RN-189i).
 */
export function hayMonedaEnCeroPorCancelados(datos) {
  if (!datos || (datos.totalRegistros ?? 0) <= 0) return false;

  return (datos.totalesPorMoneda ?? []).some((total) => total.cantidadMovimientos === 0);
}

/**
 * Historial de movimientos de TODO EL HOGAR (RF-053, RF-057).
 *
 * DECISIONES QUE ESTA PANTALLA HACE CUMPLIR, y ninguna se decide aquí:
 *   · ORDEN resuelto por el SERVIDOR —fecha descendente y, a igual fecha,
 *     identificador descendente—. Esta pantalla NO reordena (RF-053).
 *   · TOTALES POR MONEDA, nunca combinados, con el TRASPASO en su propio renglón y
 *     el gasto NETO de devoluciones (RN-003, RN-033, RN-083, RN-095).
 *   · Los CANCELADOS están ocultos tras un control y, aunque se muestren, LOS
 *     TOTALES NO CAMBIAN: los calcula el servidor sobre los activos (D-42, D-48).
 *     Los BORRADOS no aparecen ni con el control: ya no existen (D-44, RN-091a).
 *   · El filtro predeterminado es EL MES EN CURSO DEL DISPOSITIVO: se envía
 *     `fechaLocal` y el servidor decide; nunca se suple con su reloj (RN-086b).
 */
/*
 * RANGO DE FECHAS EN PANTALLA ANGOSTA (RF-088, RNF-043, D-84, RN-143).
 *
 * Van como estilo del componente y no en la hoja global a propósito: así la
 * disposición se puede afirmar en prueba de componente al ancho de referencia de
 * 320 px, sin depender de que se cargue una hoja de estilos.
 *
 * `auto-fit` con un mínimo de 160 px hace el trabajo de una media query SIN media
 * query: a 320 px de viewport no caben dos columnas y los controles se APILAN;
 * desde unos 380 px caben las dos. Los 14 px de separación garantizan que los
 * bordes no se toquen (RN-143b) y los 48 px de alto, que cada control se toque por
 * separado (RN-143c). `minWidth: 0` es lo que impide el desborde horizontal
 * (RN-143d).
 */
const ESTILO_RANGO = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
  gap: 14,
};
const ESTILO_RANGO_CAMPO = { display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 };
const ESTILO_RANGO_CONTROL = { minHeight: 48, width: '100%', boxSizing: 'border-box', minWidth: 0 };

/* Cinta de chips del prototipo v13: una sola fila que se desplaza en horizontal,
   para que los filtros no se coman la pantalla del teléfono. */
const ESTILO_CINTA = {
  display: 'flex', gap: 6, overflowX: 'auto', padding: '8px 12px', alignItems: 'center',
};
const ESTILO_CINTA_ATAJOS = {
  display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 10, marginBottom: 4,
};
const ESTILO_CHIP = {
  whiteSpace: 'nowrap', minHeight: 38, padding: '0 12px', borderRadius: 999, cursor: 'pointer',
};

/*
 * FILA DE SIETE CHIPS, DE UNA SOLA SELECCIÓN (RF-096, D-104, RN-182).
 *
 * `flex: '0 0 auto'` es lo que hace legibles los siete a 320 px (RN-182g, RN-143):
 * ningún chip se encoge, la fila se DESPLAZA en horizontal y por eso «Sin
 * mensualidades» NO se abrevia hasta volverse ambigua. Va como estilo del
 * componente y no en la hoja global a propósito, para poder afirmarlo en prueba al
 * ancho de referencia sin depender de que se cargue una hoja de estilos.
 */
const ESTILO_CHIP_DE_FILTRO = { ...ESTILO_CHIP, flex: '0 0 auto', minHeight: 40 };
/* NO DEPENDE SOLO DEL COLOR (RN-158, RN-182h): el activo va en negrita y con
   `aria-checked`; los dos de reparto se distinguen además por el trazo del borde. */
const ESTILO_CHIP_ACTIVO = { fontWeight: 800 };
const ESTILO_CHIP_REPARTO = { borderStyle: 'dashed' };

/*
 * CATÁLOGO CERRADO DE SIETE, EN ESTE ORDEN (RN-182a):
 * Todo · Gastos · Ingresos · Traspasos · Devoluciones · Mensualidades · Sin mensualidades.
 *
 * Los cuatro de tipo van juntos y en el orden ya vigente; los dos de mensualidades
 * van AL FINAL porque son de otra naturaleza —hablan del REPARTO, no del tipo— y
 * juntarlos con los de tipo haría creer que «Mensualidades» es un tipo, que es
 * exactamente el error que D-99 corrigió.
 *
 * QUÉ ENVÍA CADA UNO (RN-182b): los de tipo envían `tipo` y NO `renglonesRepartidos`;
 * los dos de reparto envían `renglonesRepartidos` y NO `tipo`; «Todo» no envía
 * ninguno de los dos —nunca un `INCLUIR` colgado (RN-173g)—.
 */
const CHIPS_DEL_LISTADO = [
  { clave: 'TODO', etiqueta: 'Todo', tipo: '', renglonesRepartidos: '' },
  { clave: 'GASTO', etiqueta: 'Gastos', tipo: TIPOS.gasto, renglonesRepartidos: '' },
  { clave: 'INGRESO', etiqueta: 'Ingresos', tipo: TIPOS.ingreso, renglonesRepartidos: '' },
  { clave: 'TRASPASO', etiqueta: 'Traspasos', tipo: TIPOS.traspaso, renglonesRepartidos: '' },
  { clave: 'DEVOLUCION', etiqueta: 'Devoluciones', tipo: TIPOS.devolucion, renglonesRepartidos: '' },
  {
    clave: 'SOLO',
    etiqueta: 'Mensualidades',
    tipo: '',
    renglonesRepartidos: RENGLONES_REPARTIDOS.solo,
    esDeReparto: true,
  },
  {
    clave: 'EXCLUIR',
    etiqueta: 'Sin mensualidades',
    tipo: '',
    renglonesRepartidos: RENGLONES_REPARTIDOS.excluir,
    esDeReparto: true,
  },
];

/* Atajos de rango del prototipo. SOLO proponen dos fechas: no cambian ninguna
   regla de consulta y el servidor sigue decidiendo (RN-086b). */
const ATAJOS_DE_RANGO = [
  {
    clave: 'mes',
    titulo: 'Este mes',
    calcular: (hoy) => ({ desde: primerDiaDelMes(hoy), hasta: hoy }),
  },
  {
    clave: 'pasado',
    titulo: 'Mes pasado',
    calcular: (hoy) => {
      const [anio, mes] = hoy.split('-').map(Number);
      const anioPrevio = mes === 1 ? anio - 1 : anio;
      const mesPrevio = mes === 1 ? 12 : mes - 1;
      const dosDigitos = String(mesPrevio).padStart(2, '0');
      const ultimo = new Date(Date.UTC(anioPrevio, mesPrevio, 0)).getUTCDate();
      return { desde: `${anioPrevio}-${dosDigitos}-01`, hasta: `${anioPrevio}-${dosDigitos}-${ultimo}` };
    },
  },
  {
    clave: '30',
    titulo: 'Últimos 30 días',
    calcular: (hoy) => {
      const [anio, mes, dia] = hoy.split('-').map(Number);
      const inicio = new Date(Date.UTC(anio, mes - 1, dia - 29));
      return { desde: inicio.toISOString().slice(0, 10), hasta: hoy };
    },
  },
  {
    clave: 'anio',
    titulo: 'Este año',
    calcular: (hoy) => ({ desde: `${hoy.slice(0, 4)}-01-01`, hasta: hoy }),
  },
];

export default function ListaDeMovimientos() {
  const hoy = obtenerFechaDeNegocioHoy();
  const ubicacion = useLocation();
  const navegar = useNavigate();

  /*
   * EL CONTEXTO DE ESTA PANTALLA VIVE EN LA DIRECCIÓN, NO EN EL ESTADO (RF-147,
   * D-236, D-250, RN-381 a RN-386).
   *
   * [CAMBIADO 2026-10-03 · incremento 09] Los nueve datos del filtro vivían en un
   * `useState` y por eso NO EXISTÍAN fuera de la pantalla: al salir y volver, el
   * filtro se perdía sin que ningún botón de alta interviniera, y el botón de alta
   * no podía heredarlo aunque se le pasara algo (CR-01). Pasarle un parámetro al
   * botón habría sido maquillaje. Ahora una dirección del historial describe por
   * completo lo que se está viendo: se puede volver a ella, refrescar y llegar a lo
   * mismo. Es la condición previa de la herencia y del regreso, no un extra.
   *
   * LO QUE ESTO NO CAMBIA: una dirección SIN parámetros se comporta exactamente
   * como hoy —mes en curso del dispositivo, sin cancelados, página 1, sin chips—
   * y un parámetro desconocido se ignora sin error (RN-383); un parámetro
   * inválido se descarta y el resto se aplica (RN-384); y no se agrega ni una
   * llamada al servidor, porque la consulta se dispara por los mismos cambios de
   * filtro que hoy (RNF-081).
   */
  const contexto = useMemo(() => leerContextoDelHistorial(ubicacion.search, {
    tipos: Object.values(TIPOS),
    repartos: Object.values(RENGLONES_REPARTIDOS),
  }), [ubicacion.search]);

  const filtros = useMemo(() => ({ ...contexto, fechaLocal: hoy }), [contexto, hoy]);

  /*
   * Cambiar un filtro REEMPLAZA el paso en vez de empujarlo: el botón atrás del
   * teléfono no es «deshacer el último filtro» y salir del historial no debe
   * costar siete toques (RF-148, RN-396). Los parámetros desconocidos que ya
   * venían se conservan, porque RN-383 manda ignorarlos, no borrarlos.
   */
  const fijarFiltros = useCallback((cambio) => {
    const siguiente = typeof cambio === 'function'
      ? cambio(filtros)
      : { ...filtros, ...cambio };

    navegar(
      { pathname: ubicacion.pathname, search: escribirContextoDelHistorial(siguiente, ubicacion.search) },
      { replace: true },
    );
  }, [filtros, navegar, ubicacion.pathname, ubicacion.search]);

  /*
   * El contexto se publica para que el botón flotante del armazón lo herede
   * (RF-145, RN-387, RN-411). SE SUGIERE LA CATEGORÍA Y NADA MÁS: la cuenta no se
   * sugiere nunca, venga de donde venga el filtro (RF-153, RN-388, RN-433), y no
   * se sugiere nada cuando el filtro es de texto, de rango, de tipo, de
   * mensualidades o de cancelados.
   */
  const direccionDeEsteListado = useContextoDeListado(
    contexto.idCategoria !== '' ? { idCategoria: contexto.idCategoria } : null,
  );

  /*
   * [NUEVO 2026-10-07 · 12/16 · RF-200, patrón RF-146] AVISO DE QUE SE ELIMINÓ.
   *
   * La pantalla del movimiento, al eliminar, vuelve AQUÍ —a la dirección exacta de
   * la que se salió, con el mismo mes, la misma cuenta y los cancelados igual de
   * visibles— y trae consigo qué se eliminó. Viaja en el estado de la navegación y
   * NO en la dirección a propósito: un «&eliminado=4231` colgado en la barra
   * reapareceria al refrescar o al compartir el enlace, y el aviso volvería a
   * salir cuando ya no viene a cuento.
   *
   * ESTE ES TODO LO QUE EL LISTADO GANA EN ESTE INCREMENTO. No hay «Ver
   * eliminados», no hay marca de hueco donde estaba el renglón y no hay control
   * nuevo (D-307): el renglón desaparece sin rastro y los totales ya vienen
   * recalculados del servidor.
   */
  const eliminado = ubicacion.state?.movimientoEliminado ?? null;
  const cuentasDescongeladas = ubicacion.state?.cuentasQueVuelvenAEditables ?? [];

  const [cuentas, setCuentas] = useState([]);
  const [categorias, setCategorias] = useState([]);
  /*
   * RF-187 · Arranca en VERDADERO en el `useState` inicial, no al empezar la
   * petición (RN-562). La lista sigue siendo `[]`, nunca nula (RN-566).
   */
  const [cargandoCuentas, setCargandoCuentas] = useState(true);
  /*
   * RF-187 / RN-655 · Desde el 17 el filtro de CATEGORÍA también es el control
   * único, así que su catálogo declara su estado de carga igual que el de cuentas.
   * Se declara aparte y no se reusa el de cuentas aunque las dos consultas vivan en
   * el mismo efecto: son dos listas distintas y el día que una se pida sola, el
   * indicador de la otra no debe mentir.
   */
  const [cargandoCategorias, setCargandoCategorias] = useState(true);

  /*
   * PANELES DE FILTRO (prototipo v13): cerrados de entrada, para que lo primero
   * que se vea sea el LISTADO y no el formulario de filtros.
   */
  const [panelAbierto, setPanelAbierto] = useState(null);

  /*
   * El rango se captura en un BORRADOR y se manda con «Aplicar», como en el
   * prototipo. Así, con el rango al revés simplemente NO SE CONSULTA (RN-143e):
   * el botón queda deshabilitado y la lista anterior se queda como está, en vez
   * de disparar una consulta que el servidor rechazaría.
   */
  const [borrador, setBorrador] = useState(() => ({ desde: contexto.desde, hasta: contexto.hasta }));
  const rangoInvalido = borrador.desde !== '' && borrador.hasta !== '' && borrador.desde > borrador.hasta;

  /* El borrador sigue al rango de la dirección: al volver a un contexto con rango
     aplicado, el panel muestra ese rango y no uno vacío (RN-385, RN-390). */
  useEffect(() => {
    setBorrador({ desde: contexto.desde, hasta: contexto.hasta });
  }, [contexto.desde, contexto.hasta]);

  const hayRangoAplicado = filtros.desde !== '' || filtros.hasta !== '';
  const hayFiltroAvanzado = filtros.idCuenta !== '' || filtros.idCategoria !== '' || filtros.texto !== '';

  function aplicarRango() {
    if (rangoInvalido) return;
    fijarFiltros((anteriores) => ({ ...anteriores, desde: borrador.desde, hasta: borrador.hasta, pagina: 1 }));
    setPanelAbierto(null);
  }

  function quitarRango() {
    setBorrador({ desde: '', hasta: '' });
    fijarFiltros((anteriores) => ({ ...anteriores, desde: '', hasta: '', pagina: 1 }));
  }

  const consultar = useCallback(() => {
    const hayRango = filtros.desde !== '' || filtros.hasta !== '';

    return consultarMovimientos({
      // El rango y la fechaLocal son excluyentes: si hay rango, manda el rango.
      fechaLocal: hayRango ? undefined : filtros.fechaLocal,
      desde: filtros.desde || undefined,
      hasta: filtros.hasta || undefined,
      tipo: filtros.tipo || undefined,
      idCategoria: filtros.idCategoria || undefined,
      idCuenta: filtros.idCuenta || undefined,
      texto: filtros.texto || undefined,
      incluirCancelados: filtros.incluirCancelados,
      // SIN CHIP DE MENSUALIDADES NO VIAJA EL PARÁMETRO: volver a «Todo» no manda
      // `INCLUIR` colgado (RN-173g, RN-185a). Y con un chip puesto, PEDIR LA PÁGINA 2
      // VA AL SERVIDOR CON EL PARÁMETRO: aquí no se filtra nada (RN-176c, RN-175f).
      renglonesRepartidos: filtros.renglonesRepartidos || undefined,
      pagina: filtros.pagina,
      tamanoPagina: TAMANO_DE_PAGINA,
    });
  }, [filtros]);

  // Cuatro estados excluyentes (RF-087, RN-140 a RN-142). Cambiar un filtro vuelve
  // a CARGANDO y el resultado cero se presenta como vacío DEL FILTRO, no como "no
  // tienes movimientos" (RN-142b).
  const listado = useConsultaDeListado(consultar, (respuesta) => (respuesta?.movimientos ?? []).length === 0);
  const { datos, estado } = listado;
  const error = listado.error;
  const cargar = listado.recargar;

  useEffect(() => {
    // Catálogos de los selectores: se piden con soloActivas=false porque el
    // historial muestra también lo desactivado (RN-012, RN-059).
    (async () => {
      try {
        const lista = await consultarCuentas({ soloActivas: false });
        setCuentas(lista.cuentas);
        setCategorias(await consultarCategorias({ soloActivas: false }));
      } catch {
        // Los selectores son ayuda: si fallan, el historial sigue viéndose.
      } finally {
        /*
         * RN-567 · El indicador vuelve a FALSO acierte o falle la carga. Aquí el
         * fallo ya se traga a propósito —los selectores son ayuda y el historial no
         * depende de ellos—, así que sin este `finally` el campo de cuenta se
         * quedaría deshabilitado PARA SIEMPRE dentro de un panel de filtros que por
         * lo demás funciona: el defecto clásico de esta regla. Con la lista vacía y
         * el indicador en falso manda RN-552 y el campo simplemente no se pinta.
         */
        setCargandoCuentas(false);
        setCargandoCategorias(false);
      }
    })();
  }, []);

  function cambiar(campo, valor) {
    fijarFiltros((anteriores) => ({ ...anteriores, [campo]: valor, pagina: 1 }));
  }

  /*
   * UNA SOLA SELECCIÓN EN LA FILA (D-104, RN-173g, RN-182c), confirmada por Deisy
   * Paez el 2026-09-03. Activar cualquier chip APAGA todos los demás —incluidos los
   * dos de mensualidades entre sí— porque cada uno fija LOS DOS campos a la vez:
   * nunca quedan un `tipo` y un `renglonesRepartidos` puestos al mismo tiempo.
   *
   * Que el CONTRATO admita combinarlos y la PANTALLA no lo ofrezca es deliberado:
   * la pantalla puede crecer sin reabrir el contrato otra vez (RN-173f).
   */
  function seleccionarChip(chip) {
    fijarFiltros((anteriores) => ({
      ...anteriores,
      tipo: chip.tipo,
      renglonesRepartidos: chip.renglonesRepartidos,
      pagina: 1,
    }));
  }

  /* El chip puesto se deriva del filtro, no se guarda aparte: así no hay dos
     verdades que se puedan desincronizar. */
  const chipActivo = filtros.renglonesRepartidos !== ''
    ? filtros.renglonesRepartidos
    : (filtros.tipo !== '' ? filtros.tipo : 'TODO');

  const hayChipDeFiltro = chipActivo !== 'TODO';

  /* Vuelve a «Todo»: es la SALIDA del vacío del filtro (RN-178d, RN-142b). */
  function quitarChip() {
    seleccionarChip(CHIPS_DEL_LISTADO[0]);
  }

  const movimientos = datos?.movimientos ?? [];
  const dias = agruparPorDia(movimientos);

  /* RN-142c: mientras CARGA no se pintan totales en cero. Se calcula una sola vez y
     no dos, para que la barra y su leyenda no puedan discrepar. */
  const totalesVisibles = estado === ESTADO_DE_LISTADO.CARGANDO
    ? []
    : (datos?.totalesPorMoneda ?? []);

  /* LEYENDA DE D-111, UNA SOLA POR BARRA (RN-189e). Se deriva de `datos`, que es la
     respuesta CUYAS CIFRAS SE ESTÁN MOSTRANDO: durante una recarga la barra sigue
     enseñando los totales previos marcados en actualización, y la leyenda acompaña a
     esas cifras y no a las que están por llegar (RN-189g, RN-165d). */
  const mostrarLeyendaDeCancelados = totalesVisibles.length > 0
    && hayMonedaEnCeroPorCancelados(datos);
  const totalPaginas = Math.max(1, Math.ceil((datos?.totalRegistros ?? 0) / TAMANO_DE_PAGINA));

  /*
   * RN-390b. Si se regresa a un contexto cuya página YA NO EXISTE porque el
   * conjunto cambió —se canceló o se borró lo que la llenaba—, se vuelve a la
   * PÁGINA 1 DEL MISMO FILTRO, no a un filtro distinto. La condición es estrecha
   * a propósito: solo corrige una página fuera de rango y nunca toca el resto del
   * contexto.
   */
  useEffect(() => {
    if (datos && filtros.pagina > totalPaginas) fijarFiltros({ pagina: 1 });
  }, [datos, filtros.pagina, totalPaginas, fijarFiltros]);

  return (
    <Shell titulo="Movimientos" subtitulo="Historial de todo el hogar">
      <ListadoActualizando visible={listado.actualizando} />

      {/* RF-200 · Se eliminó un movimiento y se volvió aquí. El aviso dice lo que
          pasó y lo que NO se puede hacer al respecto, sin jerga y sin ofrecer un
          «deshacer» que no existe (D-303, H-02). */}
      {eliminado !== null && (
        <div className="aviso n" role="status" data-prueba="aviso-movimiento-eliminado">
          <b>Listo. El movimiento se eliminó.</b> Ya no aparece en esta lista, ni en los cancelados,
          ni en ningún total. Si fue un error, tendrás que capturarlo otra vez.
          {cuentasDescongeladas.length > 0 && (
            <div style={{ marginTop: 4 }}>
              Esa cuenta se quedó <b>sin movimientos</b>: ya puedes volver a cambiarle el tipo, la
              moneda y el importe con el que arrancó.
            </div>
          )}
        </div>
      )}

      {/*
        BARRA DE FILTROS COMPACTA (prototipo v13).
        DEFECTO CORREGIDO EL 2026-08-31: los cinco filtros se pintaban desplegados
        en una sola tarjeta y ocupaban la pantalla entera del teléfono, así que la
        lista quedaba fuera de vista. El prototipo presenta una CINTA DE CHIPS y
        los filtros dentro de paneles que se abren cuando hacen falta, con el
        listado visible de entrada. No se quita ni se cambia ningún filtro ya
        aprobado en el 3/6: se cambia dónde viven.
      */}
      {/*
        LA FILA DE LOS SIETE CHIPS (RF-096, D-102, D-104, RN-182), en su orden
        cerrado y de UNA SOLA SELECCIÓN. Va en su PROPIA fila, como en el prototipo
        v16: el rango, «Más filtros…» y «Ver cancelados» NO son de esta fila y no
        compiten con ella.

        «Mensualidades» y «Sin mensualidades» NO SON CHIPS DE TIPO: no envían `tipo`,
        envían `renglonesRepartidos` (RN-182b). Se dibujan al final porque hablan del
        REPARTO, no del tipo; ponerlos entre los de tipo haría creer que
        «Mensualidad» es un quinto tipo, que es el error que D-99 corrigió.
      */}
      <div style={ESTILO_CINTA} data-prueba="chips-de-filtro"
        role="radiogroup" aria-label="Filtro del listado, una sola selección">
        {CHIPS_DEL_LISTADO.map((chip) => {
          const activo = chipActivo === chip.clave;

          return (
            <button
              key={chip.clave}
              type="button"
              role="radio"
              aria-checked={activo}
              // NOMBRE ACCESIBLE CON SU ESTADO (RNF-045b, RN-182h): quien no ve el
              // color oye igual si el filtro está puesto.
              aria-label={`${chip.etiqueta}, filtro ${activo ? 'activo' : 'inactivo'}`}
              data-prueba={`chip-${chip.clave}`}
              className={`chip${activo ? (chip.esDeReparto ? ' mo' : ' ok') : ''}`}
              style={{
                ...ESTILO_CHIP_DE_FILTRO,
                ...(chip.esDeReparto && !activo ? ESTILO_CHIP_REPARTO : null),
                ...(activo ? ESTILO_CHIP_ACTIVO : null),
              }}
              onClick={() => seleccionarChip(chip)}
            >
              {chip.etiqueta}
            </button>
          );
        })}
      </div>

      <div style={ESTILO_CINTA} data-prueba="cinta-de-filtros">
        <button
          type="button"
          className={`chip${hayRangoAplicado ? ' ok' : ''}`}
          style={ESTILO_CHIP}
          aria-expanded={panelAbierto === 'rango'}
          onClick={() => setPanelAbierto(panelAbierto === 'rango' ? null : 'rango')}
        >
          📅 Rango de fechas
        </button>
        <button
          type="button"
          className={`chip${hayFiltroAvanzado ? ' ok' : ''}`}
          style={ESTILO_CHIP}
          aria-expanded={panelAbierto === 'mas'}
          onClick={() => setPanelAbierto(panelAbierto === 'mas' ? null : 'mas')}
        >
          Más filtros…
        </button>
      </div>

      {panelAbierto === 'rango' && (
        <div className="card" style={{ margin: 12 }}>
          {/*
            RANGO DE FECHAS LEGIBLE EN PANTALLA ANGOSTA (RF-088, D-84, RN-143).
            Dos controles con ETIQUETA VISIBLE propia —"Desde" y "Hasta"—, apilados
            en una sola columna por debajo de 380 px, separados 14 px para que sus
            bordes no se toquen, 48 px de alto para que cada uno se toque por
            separado, y `min-width: 0` para que no desborden a 320 px.
          */}
          <div className="campo" data-prueba="rango-de-fechas">
            <label htmlFor="desde">RANGO DE FECHAS</label>

            {/* Atajos del prototipo: rellenan las dos fechas de una vez. Sólo
                proponen fechas; no cambian ninguna regla de consulta. */}
            <div style={ESTILO_CINTA_ATAJOS} data-prueba="atajos-de-rango">
              {ATAJOS_DE_RANGO.map((atajo) => (
                <button
                  key={atajo.clave}
                  type="button"
                  className="chip"
                  style={ESTILO_CHIP}
                  onClick={() => setBorrador(atajo.calcular(hoy))}
                >
                  {atajo.titulo}
                </button>
              ))}
            </div>

            <div style={ESTILO_RANGO}>
              {/* CAMPO ÚNICO DE FECHA DEL NÚCLEO (RF-149, RN-400): tocar
                  cualquier parte abre el selector, no solo el icono. Esta pantalla
                  ya no declara `input type="date"` propio (RNF-076). */}
              <div style={ESTILO_RANGO_CAMPO}>
                <label className="ayuda" htmlFor="desde">Desde</label>
                <CampoDeFecha id="desde" name="desde" valor={borrador.desde}
                  datoDePrueba="campo-de-fecha-desde"
                  style={ESTILO_RANGO_CONTROL}
                  aria-invalid={rangoInvalido}
                  alCambiar={(valor) => setBorrador((previo) => ({ ...previo, desde: valor }))} />
              </div>
              <div style={ESTILO_RANGO_CAMPO}>
                <label className="ayuda" htmlFor="hasta">Hasta</label>
                <CampoDeFecha id="hasta" name="hasta" valor={borrador.hasta}
                  datoDePrueba="campo-de-fecha-hasta"
                  style={ESTILO_RANGO_CONTROL}
                  aria-invalid={rangoInvalido}
                  alCambiar={(valor) => setBorrador((previo) => ({ ...previo, hasta: valor }))} />
              </div>
            </div>

            {/* El mensaje va PEGADO al campo, visible sin desplazar la pantalla
                (RN-143e), y con el rango al revés NO se consulta. */}
            {rangoInvalido && (
              <span className="err" role="alert" data-prueba="rango-invalido">
                La fecha inicial no puede ser posterior a la final. Corrige el rango para consultar.
              </span>
            )}

            <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
              <button className="btn sec" type="button" onClick={quitarRango}>Quitar el rango</button>
              <button className="btn" type="button" disabled={rangoInvalido} onClick={aplicarRango}>
                Aplicar
              </button>
            </div>

            <span className="ayuda">
              Sin rango se aplica <b>el mes en curso de tu dispositivo</b>.
            </span>
          </div>
        </div>
      )}

      <div className="card" style={{ margin: 12, display: panelAbierto === 'mas' ? 'block' : 'none' }}>
        <div className="campo">
          <label htmlFor="tipo">TIPO</label>
          {/* Elegir un tipo aquí APAGA el chip de mensualidades, igual que en la
              fila: una sola selección, sin dos filtros puestos a la vez (RN-173g). */}
          <select id="tipo" name="tipo" value={filtros.tipo}
            onChange={(evento) => seleccionarChip({
              tipo: evento.target.value, renglonesRepartidos: '',
            })}>
            <option value="">Todos</option>
            <option value={TIPOS.gasto}>Gasto</option>
            <option value={TIPOS.ingreso}>Ingreso</option>
            <option value={TIPOS.traspaso}>Traspaso</option>
            <option value={TIPOS.devolucion}>Devolución</option>
          </select>
        </div>

        {/* H-19 · Cuarto y último consumidor del combobox (RF-180). Aquí el campo
            NO es obligatorio: dejarlo sin elegir significa «todas», y para eso está
            el control de abajo. Cambia el control, no el filtro: sigue encontrando
            la cuenta EN EL ORIGEN O EN EL DESTINO (RN-528). */}
        <ComboboxDeCuenta
          id="idCuenta"
          etiqueta="CUENTA O TARJETA"
          cuentas={cuentas}
          valor={filtros.idCuenta}
          alElegir={(elegido) => cambiar('idCuenta', elegido ? String(elegido) : '')}
          textoDePeticion="Todas las cuentas del hogar"
          /* RF-187 · El campo conserva su sitio y su rótulo mientras la lista
             llega, con su texto de petición ya aprobado (RN-562 a RN-564). */
          cargando={cargandoCuentas}
          datoDePrueba="combobox-cuenta-filtro"
          ayuda={(
            <>
              La encuentra <b>en el origen o en el destino</b>: un traspaso sale en el historial de las
              dos cuentas.
            </>
          )}
        />

        {filtros.idCuenta !== '' && (
          <div className="campo">
            <button
              type="button"
              className="btn sec"
              onClick={() => cambiar('idCuenta', '')}
              data-prueba="quitar-filtro-de-cuenta"
            >
              Ver las de todas las cuentas
            </button>
          </div>
        )}

        {/* T-17-01 · QUINTO CONSUMIDOR DEL CONTROL ÚNICO, con el MISMO perfil de
            categoría de las capturas (RF-245, D-366). Aquí vivía un `<select>`
            nativo con el catálogo completo —gasto, ingreso y desactivadas— que en
            el teléfono había que recorrer a dedo.
            CAMBIA EL CONTROL, NO LOS DATOS (CA-17-19): el catálogo se sigue
            pidiendo igual (`soloActivas: false`, sin tipo, porque el historial
            filtra sobre todo lo que existió) y `idCategoria` sigue viajando en la
            dirección con el mismo nombre, el mismo formato y la misma regla de
            ausencia. El sufijo « · desactivada» lo pone la función de descripción
            del perfil, no este consumidor (RN-678).
            LA SEMÁNTICA «TODAS» ES EL VALOR VACÍO (RN-677) y el campo lo dice con
            su texto de petición: dentro de la lista no hay ningún renglón «Todas
            las categorías» —esa forma está derogada (D-369, RN-680)— y toda opción
            del desplegable es una categoría del catálogo (CA-17-23). */}
        <SelectorConBuscador
          id="idCategoria"
          etiqueta="CATEGORÍA"
          perfil={PERFIL_DE_CATEGORIA}
          opciones={categorias}
          valor={filtros.idCategoria}
          alElegir={(elegida) => cambiar('idCategoria', elegida ? String(elegida) : '')}
          textoDePeticion="Todas las categorías"
          /* RF-187 · El campo conserva su sitio y su rótulo mientras el catálogo
             llega (RN-562 a RN-564). */
          cargando={cargandoCategorias}
          datoDePrueba="combobox-categoria-filtro"
        />

        {/* EL BOTÓN ESPEJO, Y ES DEL CONSUMIDOR, NO DEL CONTROL (RN-677, RN-680).
            Calco exacto del «Ver las de todas las cuentas» de arriba: aparece solo
            con filtro puesto y quita ESTE filtro dejando los demás como están. Dos
            filtros vecinos se limpian igual, que es lo que mejor se explica a quien
            usa la aplicación. */}
        {filtros.idCategoria !== '' && (
          <div className="campo">
            <button
              type="button"
              className="btn sec"
              onClick={() => cambiar('idCategoria', '')}
              data-prueba="quitar-filtro-de-categoria"
            >
              Ver todas las categorías
            </button>
          </div>
        )}

        <div className="campo">
          <label htmlFor="texto">TEXTO EN LA NOTA</label>
          <input id="texto" name="texto" type="search" value={filtros.texto}
            placeholder="Ej.: despensa"
            onChange={(evento) => cambiar('texto', evento.target.value)} />
          <span className="ayuda">Sin acentos y sin distinguir mayúsculas.</span>
        </div>
      </div>

      {/* D-69 / RN-121d: si el 4/6 no respondió, el historial se devuelve SIN
          reparto y CON UNA ADVERTENCIA VISIBLE. Nunca a medias y nunca inventando
          cifras. */}
      {datos?.avisoRepartoNoDisponible && (
        <div className="aviso a" data-prueba="reparto-no-disponible">
          No se pudo repartir las compras a meses. <b>Los renglones que ves son los movimientos tal
          como se capturaron</b>: no se inventó ninguna cifra.
        </div>
      )}

      {/* D-73 / RN-123c: UN MES FUTURO ES PROYECCIÓN, no gasto ocurrido, y se
          presenta con aspecto distinto al de un mes cerrado. LOS SALDOS Y LA DEUDA
          NUNCA SE PROYECTAN: son de hoy, siempre (RN-123g). */}
      {datos?.esPeriodoFuturo && (
        <div className="aviso i" data-prueba="aviso-de-proyeccion">
          <b>Proyección: lo que está por cobrarse.</b> Este mes todavía no ocurre, así que aquí solo
          hay mensualidades ya calculadas de compras a meses. <b>Un total de un mes futuro no es un
          gasto que ya pasó</b>, y tu saldo y tu deuda siguen siendo los de hoy.
        </div>
      )}

      {/* TOTALES POR MONEDA, NUNCA COMBINADOS (RN-003, RN-033).
          RN-142c: mientras carga no se pintan totales en cero.

          [AMPLIADO 2026-09-05 · D-107, D-108, RN-182i] LA BARRA PINTA LO QUE RECIBE,
          SIN DECIDIR: si el servidor manda dos entradas —`$0.00 MXN` y `$0.00 USD`,
          que es lo que trae una respuesta vacía sin moneda fijada—, pinta dos; si
          manda una, pinta una. LA PANTALLA NO COMPLETA MONEDAS QUE FALTEN NI OCULTA
          LAS QUE SOBREN (RN-182i.2), y NO SE OCULTA CON LA LISTA VACÍA: el mensaje
          del vacío del filtro y la barra en `$0.00` conviven, y ninguno sustituye al
          otro (D-108, RN-187f). Por eso este bloque NO mira el estado del listado
          más allá de CARGANDO: se pinta arriba del mensaje, siempre que haya
          entradas. */}
      {totalesVisibles.length > 0 && (
      <section
        data-prueba="region-de-totales"
        aria-label="Totales del listado"
        /* RN-189j: la leyenda forma parte de la DESCRIPCIÓN ACCESIBLE de la barra.
           NO se marca como `role="alert"`: no es un evento urgente, se anuncia con
           la barra cuando ésta anuncia su actualización. */
        aria-describedby={mostrarLeyendaDeCancelados ? 'leyenda-de-cancelados' : undefined}>
      {totalesVisibles.map((total) => (
        <div key={total.idMoneda} className="card" style={{ margin: 12 }} data-prueba="barra-de-totales">
          <h4 style={{ margin: '0 0 6px', fontSize: 11.5 }}>
            {(NOMBRE_DE_MONEDA[total.codigoISO] ?? total.codigoISO).toUpperCase()} · {total.codigoISO}
          </h4>
          {/* NINGÚN TOTAL FILTRADO ES «EL GASTO DEL MES», y la pantalla lo dice CON
              TEXTO (RN-175d, RN-184d). Leer el total de «Sin mensualidades» como el
              gasto verdadero es el error caro de este estado: la compra a meses con
              plan NO reaparece completa —no existe como renglón— y se sigue viendo
              en el bloque de compras a meses y en la pantalla del plan (RN-184c).
              Los totales los calcula EL SERVIDOR sobre el conjunto filtrado: esta
              pantalla NO SUMA NADA (RN-175a, RN-175f). */}
          {hayChipDeFiltro && (
            <span className="ayuda" data-prueba="totales-de-lo-filtrado">
              Es <b>el total de lo filtrado</b>, no el del mes completo.
              {chipActivo === RENGLONES_REPARTIDOS.excluir
                && ' Lo que pagas a meses sigue contando: está repartido en sus mensualidades y lo ves en el bloque de compras a meses y en la pantalla del plan.'}
            </span>
          )}
          <div className="fila"><span className="crece">Ingresos</span><b>{formatearImporte(total.totalIngresos, total.codigoISO)}</b></div>
          <div className="fila">
            <span className="crece">Gastos <small>(neto de devoluciones)</small></span>
            <b>{formatearImporte(total.totalGastos, total.codigoISO)}</b>
          </div>
          <div className="fila">
            <span className="crece">Devoluciones <small>(informativo)</small></span>
            <b>{formatearImporte(total.totalDevoluciones, total.codigoISO)}</b>
          </div>
          <div className="fila">
            <span className="crece">Traspasos <small>(no son gasto ni ingreso)</small></span>
            <b>{formatearImporte(total.totalTraspasos, total.codigoISO)}</b>
          </div>
          <div className="fila"><span className="crece">Movimientos contados</span><b>{total.cantidadMovimientos}</b></div>
        </div>
      ))}

      {/* D-111 / RN-189: POR QUÉ HAY CEROS CON RENGLONES A LA VISTA.
          Va DEBAJO de las cifras y DENTRO de la región de la barra (RN-189h), como
          texto secundario: no es aviso emergente, no es banner de página y NO
          sustituye a ninguna advertencia existente —si coincide con la de reparto no
          disponible se muestran las dos, aquélla primero (RN-181).
          UNA SOLA VEZ POR BARRA aunque sean dos las monedas afectadas, y también
          cuando otra moneda sí trae importes: por eso el texto NO afirma que todo
          esté cancelado, solo explica por qué hay ceros (RN-189e).
          320 px (RN-189j): es TEXTO —no color, no icono—, se ajusta en dos líneas
          como máximo y sin recorte, así que no lleva `nowrap` ni `text-overflow`. */}
      {mostrarLeyendaDeCancelados && (
        <p
          id="leyenda-de-cancelados"
          className="ayuda"
          data-prueba="leyenda-de-cancelados"
          style={{ margin: '-4px 12px 12px', overflowWrap: 'break-word' }}>
          {LEYENDA_DE_CANCELADOS}
        </p>
      )}
      </section>
      )}

      {estado === ESTADO_DE_LISTADO.CARGANDO && (
        <ListadoCargando renglones={5} texto="Cargando tus movimientos…" />
      )}

      {estado === ESTADO_DE_LISTADO.ERROR && (
        <ListadoConError error={error} alReintentar={cargar} />
      )}

      {/* RN-181b: con la advertencia de reparto no disponible, la pantalla MUESTRA
          LA ADVERTENCIA EN LUGAR del vacío del filtro. Si no, diría «no hay
          mensualidades este mes» cuando la verdad es «no se pudieron calcular»: es
          una cifra inventada por omisión. */}
      {estado === ESTADO_DE_LISTADO.VACIO && !datos?.avisoRepartoNoDisponible ? (
        <div className="card" style={{ margin: 12 }} data-prueba={hayChipDeFiltro ? 'vacio-del-filtro' : undefined}>
          <div style={{ textAlign: 'center', padding: '22px 16px' }}>
            {/* LO VACÍO ES EL FILTRO, NO TU HISTORIAL (RN-178d, RN-142b): nunca se
                dice «todavía no tienes movimientos», y siempre se ofrece la salida. */}
            <p>
              <b>
                {chipActivo === RENGLONES_REPARTIDOS.solo
                  ? 'Ninguna mensualidad con estos filtros.'
                  : chipActivo === RENGLONES_REPARTIDOS.excluir
                    ? 'No hay nada fuera de las mensualidades con estos filtros.'
                    : 'No hay movimientos con estos filtros.'}
              </b>
            </p>
            <p>
              Una lista vacía <b>no es un error</b>: la consulta respondió correctamente con
              cero renglones y todos los totales en cero.
            </p>
            {hayChipDeFiltro && (
              <button className="btn sec" type="button" onClick={quitarChip}>
                Quitar el filtro y ver todo
              </button>
            )}
          </div>
        </div>
      ) : estado === ESTADO_DE_LISTADO.CON_DATOS ? (
        dias.map((dia) => (
          <div key={dia.fecha} style={{ margin: '12px 12px 0' }} data-prueba="dia-de-movimientos">
            <h4 style={{ margin: '0 0 6px', fontSize: 11.5 }}>{formatearFecha(dia.fecha)}</h4>
            {/* ASIDERO ESTABLE PARA EL TERCER NIVEL (2026-10-07): una prueba de punta a
                punta necesita un punto fijo al que esperar —«ya se dibujó el listado»— y
                hasta hoy no existía ninguno, así que esperaba un selector inventado y se
                agotaba su tiempo sin decir por qué. Convención del proyecto: `data-prueba`,
                nunca `data-testid`. No cambia nada de lo que se ve. */}
            <div className="card lista" data-prueba="lista-movimientos">
              {dia.movimientos.map((movimiento) => (
                <FilaDeMovimiento
                  key={`${movimiento.idMovimiento}-${movimiento.idMensualidad ?? 0}`}
                  movimiento={movimiento}
                  volverA={direccionDeEsteListado}
                />
              ))}
            </div>
          </div>
        ))
      ) : null}

      {/* D-69 / RN-120f: LA COMPRA NO DESAPARECE DE SU MES, SE MUEVE DE SITIO. Es
          INFORMATIVO Y NO SUMA EN NINGÚN TOTAL. Sin este apartado, alguien
          compraría el 20 de agosto, abriría agosto y creería que no se guardó. */}
      {(datos?.comprasAMesesDelPeriodo ?? []).length > 0 && (
        <div className="card lista" style={{ margin: 12 }} data-prueba="compras-a-meses-del-periodo">
          <h4 style={{ margin: '0 0 6px', fontSize: 11.5 }}>COMPRAS A MESES HECHAS ESTE MES</h4>
          <span className="ayuda">
            Se cobran <b>repartidas en los meses de sus cortes</b>, así que <b>no suman en los totales
            de arriba</b>. Aquí están para que no parezcan perdidas.
          </span>
          {datos.comprasAMesesDelPeriodo.map((compra) => (
            <Link className="fila" key={compra.idMovimiento} to={`/movimientos/${compra.idMovimiento}`}>
              <div className="crece">
                <b>Compra a {compra.numeroDeMeses} meses</b>
                <span className="chip">{compra.contadorInicial}</span>
                <small>{formatearFecha(compra.fecha)}</small>
              </div>
              <span className="imp">
                {formatearImporte(compra.importeDelMovimiento, compra.codigoISO)}
                <small>no suma aquí</small>
              </span>
            </Link>
          ))}
        </div>
      )}

      {/* EL CONTROL SE DIBUJA SIEMPRE, EN LOS TRES ESTADOS DEL FILTRO Y VALGA LO QUE
          VALGA EL CONTADOR (RN-174d, RN-186d). Su visibilidad NO se decide con
          `cantidadCancelados`: un cero no es permiso para ocultarlo ni para
          deshabilitarlo. Un control que aparece y desaparece según un número es
          justo el efecto que esa regla impide, así que aquí no hay condición. */}
      <div style={{ margin: '10px 12px' }}>
        <button
          className="btn sec ancho"
          type="button"
          aria-pressed={filtros.incluirCancelados}
          onClick={() => cambiar('incluirCancelados', !filtros.incluirCancelados)}
        >
          {filtros.incluirCancelados ? 'Ocultar' : 'Ver'} cancelados ({datos?.cantidadCancelados ?? 0})
        </button>

        {/* [AMPLIADO 2026-09-04 · D-105, RN-186e] EL CERO CON «MENSUALIDADES» NO SE
            DEJA MUDO. Con este filtro puesto el contador vale cero aunque el mes sí
            tenga cancelados, porque una mensualidad cancelada no existe como renglón
            (RN-116, D-69 iii). Sin esta línea, «Ver cancelados (0)» se leería como
            «no tienes cancelados», que es una afirmación FALSA sobre el mes: la
            regla es que el cero se explique, no que se disimule.

            Forma decidida por Deisy Paez el 2026-09-04: una LÍNEA JUNTO AL CONTROL.
            NO se pinta el control en gris ni se mete la leyenda dentro de él, que
            era la alternativa descartada: deshabilitarlo contradiría RN-186d. */}
        {filtros.renglonesRepartidos === RENGLONES_REPARTIDOS.solo
          && datos && (datos.cantidadCancelados ?? 0) === 0 && (
          <span className="ayuda" data-prueba="cancelados-cero-por-mensualidades">
            Con <b>Mensualidades</b> puesto, ese cero <b>no quiere decir que no tengas
            cancelados</b>: una mensualidad cancelada <b>no aparece como renglón</b>, así
            que aquí no hay ninguna que mostrar. Si cancelaste movimientos este mes,
            <b> quita el filtro</b> para verlos.
          </span>
        )}
      </div>

      {filtros.incluirCancelados && (
        <div className="aviso i">
          <b>Estás viendo también los cancelados.</b> Aparecen marcados, con la fecha, quién los canceló
          y su motivo. <b>Los totales de arriba no se movieron ni un centavo</b>: se calculan siempre
          sobre los movimientos activos. Un cancelado <b>sí sigue contando como
          movimiento de su cuenta</b>, así que <b>no</b> descongela su saldo inicial: eso solo
          pasa al <b>borrar</b>. <b>Los borrados no aparecen aquí ni con este control</b>: ya
          no existen, y su rastro está en la bitácora.
        </div>
      )}

      {totalPaginas > 1 && (
        <div style={{ margin: '10px 12px' }}>
          <button className="btn sec" type="button" disabled={filtros.pagina <= 1}
            onClick={() => fijarFiltros((a) => ({ ...a, pagina: a.pagina - 1 }))}>
            Anterior
          </button>{' '}
          <span className="ayuda">Página {filtros.pagina} de {totalPaginas}</span>{' '}
          <button className="btn sec" type="button" disabled={filtros.pagina >= totalPaginas}
            onClick={() => fijarFiltros((a) => ({ ...a, pagina: a.pagina + 1 }))}>
            Siguiente
          </button>
        </div>
      )}

      {/* [RETIRADO 2026-10-03 · D-248, RN-431, T-02 de CR-10] Aquí vivía el botón
          «+ Capturar» con su panel desplegable de cuatro opciones y su texto de
          ayuda. Se retiró COMPLETO —botón, panel, estado `menuAbierto` y la marca
          de prueba que lo nombraba—: el menú de tipos es ahora componente único
          del núcleo y queda UNA SOLA PUERTA, el botón flotante del armazón
          (RN-407, RN-408).
          No queda hueco visual al final de la lista y ninguna pantalla pierde su
          acceso a la captura: en escritorio la puerta es el enlace lateral, que
          abre el mismo menú (RN-431a, RN-431b).
          El texto de ayuda que decía «la cuenta viene preseleccionada con la
          última que usaste en este dispositivo» NO se promovió al menú del núcleo:
          D-247 lo volvió falso y no se reemplazó por otro (RN-407). */}
    </Shell>
  );
}

/*
 * [AMPLIADO 2026-10-07 · 12/16 · RF-148, RN-390] `volverA` es la dirección EXACTA
 * de este listado, con sus filtros, y viaja al detalle en `volverAlContexto`. Sin
 * ella, eliminar un movimiento devolvía al historial PELADO —mes en curso, sin
 * cuenta y sin cancelados—, y la persona tenía que rearmar su filtro para
 * comprobar que lo que acababa de eliminar ya no estaba.
 *
 * Se reusa la pieza del núcleo que ya resuelve esto (`rutaDeAltaDesde`): no se
 * escribe una segunda forma de pegar el parámetro, que es exactamente lo que el
 * incremento 09 vino a evitar. Su nombre habla de altas porque ese fue su primer
 * consumidor; lo que hace es heredar contexto, y eso vale igual aquí.
 */
function FilaDeMovimiento({ movimiento, volverA = null }) {
  const presentacion = presentacionDelImporte(movimiento);
  const rutaDelDetalle = volverA
    ? rutaDeAltaDesde(`/movimientos/${movimiento.idMovimiento}`, volverA)
    : `/movimientos/${movimiento.idMovimiento}`;

  // RENGLÓN REPARTIDO (D-69, RN-120a): una compra a meses con plan NO aparece en el
  // mes de la compra; aparece un renglón por mensualidad en el mes de su corte. El
  // servidor ya decidió cuáles son y con qué importe: la pantalla NO proyecta, NO
  // divide y NO concatena el contador (RNF-038).
  //
  // AL TOCARLO SE ABRE EL DETALLE DEL GASTO, que muestra el importe completo: un
  // renglón proyectado no ofrece acciones (RN-120e).
  const repartido = movimiento.esRenglonRepartido === true;

  // [AMPLIADO · incremento 19/21 · D-425, RN-736c, RN-736e] EL RENGLÓN DE UN PLAN A
  // MESES EN CURSO se pinta con ESTA MISMA presentación —misma fila, mismo contador,
  // misma referencia de la compra— pero NO ABRE NADA: no hay gasto capturado detrás
  // (capturarlo está prohibido, RN-711), así que no hay detalle que mostrar y la
  // pantalla NO GANA NAVEGACIÓN. Se dibuja como `div` en lugar de enlace, sin
  // `data-id`, porque su `idMovimiento` no identifica nada.
  const sinDetalle = movimiento.esPlanEnCurso === true;

  const Contenedor = sinDetalle ? 'div' : Link;
  const propsDelContenedor = sinDetalle
    ? { 'data-prueba': 'renglon-repartido', 'data-en-curso': 'true' }
    : {
        to: rutaDelDetalle,
        // [AMPLIADO 2026-10-07] Un renglón normal también se puede nombrar, y lleva
        // su identificador: es como una prueba comprueba que el movimiento que acaba
        // de eliminar YA NO ESTÁ, sin depender del texto que muestre.
        'data-prueba': repartido ? 'renglon-repartido' : 'renglon-movimiento',
        'data-id': movimiento.idMovimiento,
      };

  return (
    <Contenedor
      className="fila"
      {...propsDelContenedor}
      style={
        movimiento.cancelado || movimiento.esDeTarjetaDesactivada
          ? { opacity: 0.58 }
          : undefined
      }
    >
      <div className="crece">
        <b style={movimiento.cancelado ? { textDecoration: 'line-through' } : undefined}>
          {tituloDelMovimiento(movimiento)}
        </b>
        {/* CONTADOR YA ARMADO POR EL SERVIDOR (D-65, RNF-038). */}
        {repartido && <span className="chip" data-prueba="contador">{movimiento.contadorDeMensualidad}</span>}
        {!repartido && movimiento.esMesesSinIntereses && <span className="chip">MSI</span>}
        {movimiento.tipo === TIPOS.devolucion && <span className="chip">DEVOLUCIÓN</span>}
        {movimiento.cancelado && <span className="chip">CANCELADO</span>}
        <small>
          {movimiento.nombreCuenta}
          {movimiento.nombreCuentaDestino && ` → ${movimiento.nombreCuentaDestino}`}
          {' · '}{movimiento.nombrePropietario}
          {movimiento.nota && ` · ${movimiento.nota}`}
          {/* La compra completa viaja como REFERENCIA y NO SE SUMA: si se sumara
              junto al imputado, el mes contaría 13 000 (RN-118e). */}
          {repartido && ` · compra del ${formatearFecha(movimiento.fechaDelMovimiento)} por ${formatearImporte(movimiento.importeDelMovimiento, movimiento.codigoISO)}`}
        </small>
        {/* D-72 / RN-121e: SIGUE SUMANDO en el gasto del mes; la leyenda existe para
            que nadie lo confunda con algo pendiente de pagar. */}
        {movimiento.esDeTarjetaDesactivada && (
          <small data-prueba="tarjeta-desactivada">Tarjeta desactivada · ya pagado</small>
        )}
      </div>
      {/* IMPORTE LIMPIO (RF-138, RN-343 a RN-346). Nada se concatena al importe
          formateado: la divisa ya va dentro y el tipo ya está en el título del
          renglón. La leyenda que sobrevive se pinta en su propio renglón, y si no
          hay leyenda NO se dibuja el elemento vacío ni se reserva el espacio. */}
      {/* RF-215, RN-614 (D-254) · EL COLOR POR TIPO, QUE FALTABA. La clase ya la
          decidía `presentacionDelImporte` desde el 3/6 y nunca llegaba al marcado:
          todos los importes se pintaban igual. Gasto rojo con «−», ingreso y
          devolución verdes con «+», traspaso azul sin signo, siempre por tokens
          para que valga en los dos temas. El cancelado conserva su presentación
          atenuada aprobada (D-111), que no se toca. */}
      <span className={`imp ${claseDeColorDelImporte(movimiento)}`.trim()} data-prueba="importe-del-renglon">
        {presentacion.signo}{formatearImporte(movimiento.importe, movimiento.codigoISO)}
        {(movimiento.cancelado || presentacion.leyenda) && (
          <small data-prueba="leyenda-del-importe">
            {movimiento.cancelado ? 'no cuenta' : presentacion.leyenda}
          </small>
        )}
      </span>
    </Contenedor>
  );
}
